import { useCallback, useRef, useState, type ReactNode } from 'react';
import { Link } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  SRS_STATES,
  scheduleReview,
  type SrsRating,
  type SrsState,
  type WordRecordView,
} from '@en3/shared';
import { queryKeys } from '@/api/client';
import { toDisplayMessage } from '@/api/errors';
import { useAuthStatus } from '@/api/hooks/useAuth';
import { reviewApi, type ReviewSubmitItem } from '@/api/review.api';
import { ROUTES } from '@/app/routes';
import { EmptyBlock, ErrorBlock, LoadingBlock } from '@/components/common/States';
import { ReviewFlashcard } from '@/components/review/ReviewFlashcard';
import { ReviewSummaryView } from '@/components/review/ReviewSummaryView';
import { Button } from '@/components/ui/button';
import { Select } from '@/components/ui/card';
import { Field } from '@/components/ui/field';
import { AuthRequiredNotice } from '@/pages/WordbookPage';

/**
 * 复习页：取队列 → 逐张自评 → 结算。
 *
 * **核心取舍：评分只留在内存里，会话结束时一次性提交。**
 * - 好处：连续刷卡不卡（移动网络下每张一次往返会明显卡顿）；中途退出时"没答完的不算数"，
 *   不会留下半截调度状态（详见 `api/review.api.ts` 的说明）。
 * - 代价：提交失败会丢一整轮进度，所以这里必须做到两件事：
 *   1. `items` 在**提交成功之前绝不清空**（失败后"重试提交"用的就是这份数据）；
 *   2. 失败提示里明确写出"数据还在本地、不要刷新页面"，而不是只丢一句"提交失败"。
 *
 * 评分后的调度解释由**前端预算**：用前后端共享的纯函数 `scheduleReview`（同一份算法），
 * 输入就是卡片上现有的 srsState/ease/intervalDays/reps/lapses/familiarity。
 * 之所以不用后端返回的 `results[].explanation`：后端只在整个会话结束时批量调度，
 * 而这句话要在"评完这一张、马上看下一张"的瞬间出现；两处口径同源，展示上不冲突。
 *
 * 状态机：`idle`（队列概览 + 开始）→ `studying`（逐张）→ `summary`（结算）。
 * 它是本地 state 而不是全局 store：刷新页面后"会话进行到一半"没有可恢复的语义，
 * 强行恢复只会让用户面对一张已经看过答案的卡。
 *
 * **标题约束（E2E `app-shell.spec.ts`）**：`/review` 的一级标题必须包含「复习」，
 * 且**任何状态**下都要在（加载中 / 未登录 / 空队列 / 出错 / 复习中 / 结算）。
 * 所以下面把 `<h1>` 放在登录门与阶段分支之外，只切换 h1 下方的内容区；
 * 进度、统计这类动态信息一律放 h1 下方，绝不拼进 h1 文本。
 */
const QUEUE_LIMIT = 20;

/**
 * 单张卡停留时长的上报上限，与 `reviewSubmitItemSchema` 的 `elapsedMs` 上限保持一致。
 * 必须在这里截断：用户把一张卡开着去接了杯水，elapsedMs 就会超过 10 分钟，
 * 而 zod 会因此拒掉**整批**提交 —— 为了一个纯统计字段丢掉一轮复习显然不划算。
 */
const MAX_ELAPSED_MS = 10 * 60 * 1000;

/**
 * 复习页入口：登录门 + 会话。
 *
 * 登录态判断与数据请求分开的理由：未登录时不该建立任何复习队列查询
 * （否则会先打一个必然 401 的请求再显示引导），hook 顺序也不受登录态影响。
 */
export function ReviewPage() {
  const status = useAuthStatus();

  // 三个分支共用同一个 h1：恢复登录态时不渲染"未登录"，也不隐藏标题
  if (status === 'idle' || status === 'loading') {
    return (
      <section className="mx-auto max-w-2xl px-5 py-10">
        <h1 className="text-2xl font-bold">复习</h1>
        <p role="status" className="mt-2 text-sm text-muted-foreground">
          正在恢复登录状态…
        </p>
      </section>
    );
  }

  if (status !== 'authenticated') {
    // AuthRequiredNotice 自带同名的 h1（只有功能名，不含附加文案），未登录态下标题同样在
    return (
      <AuthRequiredNotice
        feature="复习"
        description="复习记录需要登录后才会保存：哪种评分、什么时候该再见一次，都要记在你的账号下才能算。"
      />
    );
  }

  return <ReviewSession />;
}

/** 队列概览里的一格。 */
function Tile({ label, value }: { label: string; value: number }) {
  return (
    <div className="rounded-md border p-3">
      <dt className="text-xs text-muted-foreground">{label}</dt>
      <dd className="mt-1 text-xl font-semibold tabular-nums">{value}</dd>
    </div>
  );
}

/** 提交流程的失败面板。数据还在内存里，所以只提供"重试"，不提供"丢弃"。 */
function SubmitErrorPanel({
  count,
  message,
  pending,
  onRetry,
}: {
  count: number;
  message: string;
  pending: boolean;
  onRetry: () => void;
}) {
  return (
    <div className="mt-4 rounded-lg border border-destructive/40 bg-destructive/5 p-4">
      <p role="alert" className="text-sm text-destructive">
        提交失败：{message}
      </p>
      <p className="mt-1 text-xs text-muted-foreground">
        本次已评分的 {count} 张仍保留在当前页面内存里，重试不会丢。请不要刷新或关闭页面。
      </p>
      <Button variant="outline" className="mt-3" loading={pending} onClick={onRetry}>
        重试提交
      </Button>
    </div>
  );
}

/** `record.srsState` 在接口层是字符串（后端枚举列），这里收窄成联合类型再交给调度算法。 */
function toSrsState(value: string): SrsState {
  return (SRS_STATES as readonly string[]).includes(value) ? (value as SrsState) : 'new';
}

/**
 * 评分 → 一句人话解释（"记得 → 下次 3 天后"）。
 *
 * 用的是**前后端共享的同一份调度算法** `scheduleReview`，输入就是卡片上现有的 srs 字段。
 * 之所以在前端算而不是等后端返回：后端只在会话结束时批量调度，而这句话必须在
 * "刚评完这一张"的瞬间出现；两处同源，结论不会打架。
 */
function explainRating(record: WordRecordView, rating: SrsRating, now: number): string {
  const next = scheduleReview(
    {
      srsState: toSrsState(record.srsState),
      ease: record.ease,
      intervalDays: record.intervalDays,
      reps: record.reps,
      lapses: record.lapses,
      familiarity: record.familiarity,
    },
    rating,
    now,
  );
  return `${record.surface}：${next.explanation}`;
}

function ReviewSession() {
  const queryClient = useQueryClient();
  const [mode, setMode] = useState<'due' | 'mixed'>('due');
  const [phase, setPhase] = useState<'idle' | 'studying' | 'summary'>('idle');
  const [index, setIndex] = useState(0);
  /** 已评分但**尚未成功提交**的卡片：成功前不清空（见文件头取舍说明） */
  const [items, setItems] = useState<ReviewSubmitItem[]>([]);
  const [lastExplanation, setLastExplanation] = useState<string | null>(null);
  /** 本张卡展示时刻，用于 elapsedMs（只做统计，不参与调度） */
  const cardShownAtRef = useRef(0);

  const queueParams = { limit: QUEUE_LIMIT, mode };
  const queue = useQuery({
    queryKey: queryKeys.reviewQueue(queueParams),
    queryFn: () => reviewApi.queue(queueParams),
  });

  const cards = queue.data?.items ?? [];
  const current = cards[index] ?? null;

  const submit = useMutation({
    mutationFn: (payload: ReviewSubmitItem[]) => reviewApi.submit(payload),
    onSuccess: async () => {
      // 只有服务端确认落库后才清空本地这批评分
      setItems([]);
      setPhase('summary');
      // 复习会同时改变生词本状态、首页统计与水平特征，四个前缀一起失效（见 api/client 的键约定）
      await Promise.all([
        queryClient.invalidateQueries({ queryKey: ['review'] }),
        queryClient.invalidateQueries({ queryKey: ['wordbook'] }),
        queryClient.invalidateQueries({ queryKey: ['me'] }),
        queryClient.invalidateQueries({ queryKey: ['level'] }),
      ]);
    },
  });

  const startSession = useCallback(() => {
    setItems([]);
    setIndex(0);
    setLastExplanation(null);
    cardShownAtRef.current = Date.now();
    submit.reset();
    setPhase('studying');
  }, [submit]);

  /** 结束并提交。一张都没评过就不发请求（后端要求 items 至少 1 条），直接回概览。 */
  const finish = useCallback(
    (payload: ReviewSubmitItem[]) => {
      if (payload.length === 0) {
        setPhase('idle');
        return;
      }
      submit.mutate(payload);
    },
    [submit],
  );

  const exit = useCallback(() => finish(items), [finish, items]);

  const rate = useCallback(
    (rating: SrsRating) => {
      if (!current) return;
      const record = current.record;
      const now = Date.now();
      const next = [
        ...items,
        {
          recordId: record.id,
          rating,
          elapsedMs: Math.min(MAX_ELAPSED_MS, Math.max(0, now - cardShownAtRef.current)),
        },
      ];

      setItems(next);
      // 评分 → 下一张之前先给出调度解释，用户才知道"点忘记"与"点秒答"的差别在哪
      setLastExplanation(explainRating(record, rating, now));
      setIndex(index + 1);
      cardShownAtRef.current = now;

      if (index + 1 >= cards.length) finish(next);
    },
    [cards.length, current, finish, index, items],
  );

  const summary = submit.data?.summary;

  /** 三个阶段的差异**只体现在 h1 下方**；h1 本身在最后的 return 里无条件渲染。 */
  function renderBody(): ReactNode {
    if (phase === 'summary' && summary) {
      return (
        <ReviewSummaryView
          summary={summary}
          canContinue={cards.length > 0 && (queue.data?.summary.remainingToday ?? 0) > 0}
          onContinue={startSession}
        />
      );
    }

    if (phase === 'studying') {
      // 最后一张评完（或用户中途退出）后进入提交中：此时没有"当前卡"，只在等提交结果
      if (!current && !submit.isError) return <LoadingBlock label="正在提交本次复习…" />;

      const total = cards.length;
      const done = Math.min(index, total);
      const percent = total > 0 ? Math.round((done / total) * 100) : 0;

      return (
        <>
          <div className="flex items-center justify-between gap-3">
            <p className="text-sm text-muted-foreground">
              第 {Math.min(index + 1, total)} / {total} 张
            </p>
            <Button
              variant="outline"
              size="sm"
              loading={submit.isPending}
              onClick={() => finish(items)}
              aria-label="结束并提交本次复习"
            >
              结束并提交
            </Button>
          </div>

          <div
            role="progressbar"
            aria-label="本次复习进度"
            aria-valuemin={0}
            aria-valuemax={total}
            aria-valuenow={done}
            className="mt-2 h-1.5 overflow-hidden rounded-full bg-muted"
          >
            <div className="h-full rounded-full bg-primary transition-all" style={{ width: `${percent}%` }} />
          </div>

          {lastExplanation && (
            <p role="status" className="mt-3 rounded-md border bg-muted/40 px-3 py-2 text-xs">
              上一张：{lastExplanation}
            </p>
          )}

          {submit.isError && (
            <SubmitErrorPanel
              count={items.length}
              message={toDisplayMessage(submit.error)}
              pending={submit.isPending}
              onRetry={() => submit.mutate(items)}
            />
          )}

          {current && (
            <ReviewFlashcard
              // key 让"翻面状态"随卡片切换而重挂载，不必用 effect 手动重置
              key={`${current.record.id}-${index}`}
              card={current}
              disabled={submit.isPending}
              onRate={rate}
              onExit={exit}
            />
          )}

          <p className="mt-4 text-center text-xs text-muted-foreground">
            键盘：空格翻面 · 0–4 评分 · Esc 结束并提交
          </p>
        </>
      );
    }

    // idle：队列概览 + 开始按钮（只有这一段会显示"开始复习"，会话中不会被误触）
    return (
      <>
        <p className="mt-1 text-sm text-muted-foreground">
          按记忆曲线安排：忘记、困难、模糊、记得、秒答五档自评，系统据此决定下次什么时候再考你。
        </p>

        <div className="mt-5 max-w-xs">
          {/* 这个选择器只在 idle 阶段渲染（会话中它不存在），因此不必额外做"锁定"；
              这里的 disabled 只为挡住"正在取队列时的连点"。 */}
          <Field
            id="review-mode"
            label="复习范围"
            hint="「只复习到期的」尊重记忆曲线；「混合」在不够时补最不熟的词。"
          >
            <Select
              id="review-mode"
              value={mode}
              onChange={(event) => setMode(event.target.value as 'due' | 'mixed')}
              disabled={queue.isFetching}
            >
              <option value="due">只复习到期的</option>
              <option value="mixed">混合（不够时补最不熟的）</option>
            </Select>
          </Field>
        </div>

        {queue.isPending ? (
          <LoadingBlock label="正在准备复习队列…" />
        ) : queue.isError ? (
          <ErrorBlock
            className="mt-4"
            message={`复习队列加载失败：${toDisplayMessage(queue.error)}`}
            onRetry={() => void queue.refetch()}
          />
        ) : cards.length === 0 ? (
          <EmptyBlock
            className="mt-4"
            title="今天没有到期的单词"
            description={
              mode === 'due'
                ? '间隔重复靠的是"恰好要忘的时候再见一次"，现在还没有到点。也可以切到「混合」模式主动多练几张。'
                : '生词本里暂时没有可复习的词。去阅读页标几个新词吧。'
            }
            action={
              <div className="flex flex-wrap justify-center gap-2">
                <Link
                  to={ROUTES.wordbook}
                  className="inline-flex h-9 items-center rounded-md border px-3 text-sm hover:bg-accent hover:text-accent-foreground"
                >
                  去生词本
                </Link>
                <Link
                  to={ROUTES.textbooks}
                  className="inline-flex h-9 items-center rounded-md border px-3 text-sm hover:bg-accent hover:text-accent-foreground"
                >
                  去阅读文章
                </Link>
              </div>
            }
          />
        ) : (
          <>
            <dl className="mt-5 grid grid-cols-2 gap-3 text-center sm:grid-cols-4">
              <Tile label="到期卡" value={queue.data?.summary.dueTotal ?? 0} />
              <Tile label="其中新卡" value={queue.data?.summary.newCount ?? 0} />
              <Tile label="今天已复习" value={queue.data?.summary.reviewedToday ?? 0} />
              <Tile label="今天还要复习" value={queue.data?.summary.remainingToday ?? 0} />
            </dl>

            <p className="mt-4 text-sm text-muted-foreground">
              本次将复习 <b className="text-foreground">{cards.length}</b> 张卡，评完一起提交。
            </p>

            <Button size="lg" className="mt-3 w-full sm:w-auto" onClick={startSession}>
              开始复习
            </Button>
          </>
        )}
      </>
    );
  }

  return (
    <section className="mx-auto max-w-2xl px-5 py-8">
      {/* h1 无条件渲染：E2E 断言 /review 的标题包含「复习」，加载中/空/出错时也必须能取到 */}
      <h1 className="text-2xl font-bold">复习</h1>
      {renderBody()}
    </section>
  );
}
